import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Video, Star, Trash2, Plus, Link2 } from "lucide-react";
import { PageHeader, SectionCard } from "../components/ui/kaizen";

export default function TechniqueVideosModule() {
  const [disciplines, setDisciplines] = useState([]);
  const [discipline, setDiscipline] = useState("");
  const [techniques, setTechniques] = useState([]);
  const [selected, setSelected] = useState(null);
  const [links, setLinks] = useState([]);
  const [videos, setVideos] = useState([]);
  const [addVideoId, setAddVideoId] = useState("");

  const loadOverview = async (disc) => {
    try {
      const { data } = await api.get("/technique-videos/overview", { params: disc ? { discipline: disc } : {} });
      setTechniques(data.techniques || []);
      if (!disciplines.length) setDisciplines(data.disciplines || []);
    } catch { toast.error("Chargement impossible"); }
  };
  const loadVideos = async () => {
    try { const { data } = await api.get("/technique-videos/available"); setVideos(data.videos || []); } catch {}
  };
  const loadLinks = async (tid) => {
    try { const { data } = await api.get(`/technique-videos/technique/${tid}`); setLinks(data.links || []); } catch {}
  };

  useEffect(() => { loadOverview(""); loadVideos(); }, []);
  useEffect(() => { if (selected) loadLinks(selected.id); }, [selected]);

  const addLink = async () => {
    if (!addVideoId || !selected) return;
    try {
      await api.post("/technique-videos", { technique_id: selected.id, video_id: addVideoId,
        is_primary: links.length === 0, display_order: links.length });
      toast.success("Vidéo liée");
      setAddVideoId("");
      loadLinks(selected.id); loadOverview(discipline);
    } catch (e) { toast.error(e?.response?.data?.detail || "Liaison impossible"); }
  };
  const setPrimary = async (l) => { await api.patch(`/technique-videos/${l.id}`, { is_primary: true }); loadLinks(selected.id); };
  const unlink = async (l) => { await api.delete(`/technique-videos/${l.id}`); loadLinks(selected.id); loadOverview(discipline); };

  return (
    <div className="max-w-6xl mx-auto" data-testid="technique-videos-module">
      <PageHeader icon={Link2} title="Vidéos par technique"
        subtitle="Reliez plusieurs vidéos à chaque technique (et réutilisez une vidéo sur plusieurs techniques). Marquez la vidéo principale." />

      <div className="mb-4 flex items-center gap-3">
        <label className="text-sm text-white/60">Discipline :</label>
        <select data-testid="discipline-filter" value={discipline}
          onChange={(e) => { setDiscipline(e.target.value); setSelected(null); loadOverview(e.target.value); }}
          className="bg-black/40 border border-white/15 px-3 py-2 text-white text-sm">
          <option value="">Toutes</option>
          {disciplines.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <SectionCard title={`Techniques (${techniques.length})`}>
          <div className="space-y-1 max-h-[520px] overflow-y-auto" data-testid="techniques-list">
            {techniques.map((t) => (
              <button key={t.id} data-testid={`technique-${t.id}`} onClick={() => setSelected(t)}
                className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 border transition-colors ${selected?.id === t.id ? "border-[#D90429] bg-[#D90429]/10 text-white" : "border-white/10 text-white/70 hover:border-white/30"}`}>
                <span className="truncate">{t.nom} <span className="text-white/40 text-xs">· {t.categorie}</span></span>
                <span className={`text-xs shrink-0 flex items-center gap-1 ${t.video_count ? "text-emerald-400" : "text-white/30"}`}>
                  <Video size={12} /> {t.video_count}
                </span>
              </button>
            ))}
          </div>
        </SectionCard>

        <SectionCard title={selected ? `Vidéos de « ${selected.nom} »` : "Sélectionnez une technique"}>
          {!selected ? (
            <p className="text-white/40 text-sm">Choisissez une technique à gauche pour gérer ses vidéos.</p>
          ) : (
            <div className="space-y-4">
              <div className="flex gap-2" data-testid="add-video-row">
                <select value={addVideoId} onChange={(e) => setAddVideoId(e.target.value)} data-testid="video-picker"
                  className="flex-1 bg-black/40 border border-white/15 px-3 py-2 text-white text-sm">
                  <option value="">— Choisir une vidéo à lier —</option>
                  {videos.map((v) => <option key={v.id} value={v.id}>{v.title} ({v.discipline})</option>)}
                </select>
                <button onClick={addLink} disabled={!addVideoId} data-testid="add-video-btn"
                  className="bg-[#D90429] hover:bg-[#b8031f] disabled:opacity-40 text-white px-4 flex items-center gap-1"><Plus size={16} /> Lier</button>
              </div>

              {links.length === 0 ? <p className="text-white/40 text-sm">Aucune vidéo liée.</p> : (
                <div className="space-y-1.5" data-testid="linked-videos">
                  {links.map((l) => (
                    <div key={l.id} className="flex items-center justify-between gap-2 text-sm border-b border-white/5 pb-1.5">
                      <span className="flex items-center gap-2 min-w-0">
                        {l.is_primary && <Star size={14} className="text-amber-400 fill-amber-400 shrink-0" />}
                        <span className="text-white/80 truncate">{l.title}</span>
                        <span className="text-white/40 text-xs shrink-0">{l.discipline}</span>
                      </span>
                      <span className="flex items-center gap-2 shrink-0">
                        {!l.is_primary && <button onClick={() => setPrimary(l)} data-testid={`primary-${l.id}`} title="Définir principale" className="text-white/30 hover:text-amber-400"><Star size={14} /></button>}
                        <button onClick={() => unlink(l)} data-testid={`unlink-${l.id}`} title="Délier" className="text-white/30 hover:text-[#D90429]"><Trash2 size={14} /></button>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </SectionCard>
      </div>
    </div>
  );
}
